import { ModuleManager } from "./ModuleManager";
import { StringData } from "../StringData";
import { StringMirrorObject } from "../../objects/StringMirrorObject";
import { StringObject } from "../../objects/StringObject";
import { EventManager } from "./EventManager";
import { ISettingsChangeData } from "../../models/event/ISettingsChangeData";
import { StringToolsContainer } from "../StringToolsContainer";
import { DOMBatcher } from "./DOMBatcher";
import { StringModule } from "../StringModule";

export class ObjectManager {
  private objects = new Map<string, StringObject>();
  private connectQueue: { id: string; element: HTMLElement }[] = [];
  private mirrors = new Map<string, StringMirrorObject>();
  private mirrorId = 1;
  private globalId = 1;
  private domBatcher = new DOMBatcher();
  private domBatcherEnabled = false;
  private inviewStarts: Array<{ pos: number; object: StringObject }> = [];
  private inviewEnds: Array<{ pos: number; object: StringObject }> = [];
  private inviewActive = new Set<StringObject>();
  private inviewStartIdx = 0;
  private inviewEndIdx = 0;
  private inviewIndexDirty = true;
  private lastInviewScrollPos = 0;
  private intersectionObserverEnabled = true;

  constructor(
    private data: StringData,
    private modules: ModuleManager,
    private events: EventManager,
    private tools: StringToolsContainer
  ) {}

  /**
   * Returns the object map (read-only).
   */
  get all(): ReadonlyMap<string, StringObject> {
    return this.objects;
  }

  /**
   * Adds a new object from an element.
   */
  public add(el: HTMLElement) {
    let idAttr = `string-${this.globalId++}`;
    let key = "string-id";

    if (el.getAttribute("string-id")) {
      idAttr = el.getAttribute("string-id")!;
      key = "string-id";
    }
    if (el.getAttribute("data-string-id")) {
      idAttr = el.getAttribute("data-string-id")!;
      key = "data-string-id";
    }

    const object =
      idAttr && this.objects.has(idAttr) ? this.objects.get(idAttr)! : new StringObject(idAttr, el);

    el.setAttribute(key, object.id);

    const keysAttr = el.getAttribute("string") ?? el.getAttribute("data-string");

    if (keysAttr) {
      object.keys = (keysAttr ?? "")
        .split("|")
        .map((s) => s.trim())
        .filter(Boolean);
    }

    el.setAttribute("string-inited", "");
    this.objects.set(object.id, object);

    const attributes = this.getAllAttributes(el);

    // Delegate core setup (dimensions, offsets, key, start/end, etc.)
    this.modules.core.forEach((m) => {
      if ("setupCoreProperties" in m && typeof m["setupCoreProperties"] === "function") {
        (m as any).setupCoreProperties(object, el, attributes);
      }
    });

    // Try connecting to modules
    const connectableModules = this.modules.core.filter(
      (m): m is StringModule => m instanceof StringModule && m.canConnect(object)
    );
    if (this.domBatcherEnabled && connectableModules.length > 0) {
      const batchContexts = connectableModules.map((module) => ({
        module,
        object,
        element: el,
        attributes,
        globalId: this.globalId,
        windowSize: this.data.viewport.windowHeight,
      }));
      this.domBatcher.batchModuleInitialization(batchContexts);
      this.domBatcher.scheduleWrite(() => {
        this.initObservers(object, el);
        this.checkInviewForObject(object);
      });
    } else {
      connectableModules.forEach((m) => {
        m.initializeObject(this.globalId, object, el, attributes);
        m.calculatePositions(object, this.data.viewport.windowHeight);
        m.connectObject(object);
        m.addObject(object.id, object);
      });
      this.initObservers(object, el);
      this.checkInviewForObject(object);
    }

    // Restore connect-from
    const queueItems = this.connectQueue.filter((q) => q.id === object.id);
    queueItems.forEach((item) => this.attachMirrorToObject(object, item.element));
    this.connectQueue = this.connectQueue.filter((q) => q.id !== object.id);
    this.inviewIndexDirty = true;
  }

  public setDOMBatcherEnabled(enabled: boolean): void {
    this.domBatcherEnabled = enabled;
    if (!enabled) {
      this.domBatcher.flushSync();
    }
  }

  public setIntersectionObserverEnabled(enabled: boolean): void {
    if (this.intersectionObserverEnabled === enabled) return;
    this.intersectionObserverEnabled = enabled;
    this.objects.forEach((object) => {
      object.getProperty<IntersectionObserver>("observer-progress")?.disconnect();
      if (enabled) {
        this.initObservers(object, object.htmlElement);
      }
    });
  }

  public invalidateInviewIndex(): void {
    this.inviewIndexDirty = true;
  }

  /**
   * Removes an object by its id.
   */
  public remove(id: string) {
    const obj = this.objects.get(id);
    if (!obj) return;

    obj.events.clearAll();
    obj.getProperty<IntersectionObserver>("observer-progress")?.disconnect();
    obj.getProperty<IntersectionObserver>("observer-inview")?.disconnect();

    obj.htmlElement.removeAttribute("string-inited");
    obj.leave();
    obj.remove();

    obj.mirrorObjects.forEach((mirror) => {
      const mirrorIds = this.getMirrorIds(mirror.htmlElement);
      this.setMirrorIds(
        mirror.htmlElement,
        mirrorIds.filter((id) => id !== mirror.id)
      );
      this.mirrors.delete(mirror.id);

      const copyFromId =
        mirror.htmlElement.getAttribute("string-copy-from") ??
        mirror.htmlElement.getAttribute("data-string-copy-from");
      if (copyFromId) {
        this.enqueueConnection(copyFromId, mirror.htmlElement);
      }
    });

    this.objects.delete(id);
    this.inviewActive.delete(obj);
    this.inviewIndexDirty = true;
  }

  /**
   * Add an element that will connect later.
   */
  public enqueueConnection(id: string, element: HTMLElement) {
    // Split by | to handle multiple IDs
    const ids = id
      .split("|")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    ids.forEach((singleId) => {
      if (this.connectQueue.some((item) => item.id === singleId && item.element === element)) {
        return;
      }
      this.connectQueue.push({ id: singleId, element });
    });
  }

  public linkMirror(id: string, element: HTMLElement): void {
    // Split by | to handle multiple IDs
    const ids = id
      .split("|")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    ids.forEach((singleId) => {
      const parent = this.objects.get(singleId);
      if (parent) {
        this.attachMirrorToObject(parent, element);
      } else {
        this.enqueueConnection(singleId, element);
      }
    });
  }

  private attachMirrorToObject(object: StringObject, element: HTMLElement): StringMirrorObject {
    const existingIds = this.getMirrorIds(element);
    for (const id of existingIds) {
      const existing = this.mirrors.get(id);
      if (existing && existing.parentObject === object) {
        return existing;
      }
    }

    const mirrorId = `string-mirror-${this.mirrorId++}`;
    const mirror = new StringMirrorObject(mirrorId, element, object);
    this.setMirrorIds(element, [...existingIds, mirrorId]);
    object.addMirror(mirror);
    this.mirrors.set(mirrorId, mirror);

    const easingAttr =
      element.getAttribute("string-easing") ?? element.getAttribute("data-string-easing");
    if (easingAttr && easingAttr.trim().length > 0) {
      mirror.setEasing(this.tools.easingFunction.process({ easing: easingAttr }));
      mirror.setProperty("easing", easingAttr);
    }
    return mirror;
  }

  private detachMirrorByElement(element: HTMLElement): void {
    const ids = this.getMirrorIds(element);
    if (ids.length === 0) return;
    ids.forEach((id) => this.detachMirrorById(id));
    this.clearMirrorIds(element);
  }

  private detachMirrorById(mirrorId: string): void {
    const mirror = this.mirrors.get(mirrorId);
    if (!mirror) return;
    mirror.parentObject.removeMirror(mirrorId);
    this.mirrors.delete(mirrorId);
  }

  private getMirrorIds(element: HTMLElement): string[] {
    const raw =
      element.getAttribute("string-mirror-id") ?? element.getAttribute("data-string-mirror-id");
    if (!raw) return [];
    return raw
      .split("|")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }

  private setMirrorIds(element: HTMLElement, ids: string[]): void {
    if (ids.length === 0) {
      this.clearMirrorIds(element);
      return;
    }
    element.setAttribute("string-mirror-id", ids.join("|"));
  }

  private clearMirrorIds(element: HTMLElement): void {
    element.removeAttribute("string-mirror-id");
    element.removeAttribute("data-string-mirror-id");
  }

  /**
   * Retrieves all attributes of a given HTML element and returns them as a record.
   *
   * @param el - The HTML element from which to extract attributes.
   * @returns A record where the keys are attribute names and the values are attribute values.
   */
  private getAllAttributes(el: HTMLElement): Record<string, any> {
    const attributes: Record<string, any> = {};
    Array.from(el.attributes).forEach((attr) => {
      attributes[attr.name] = attr.value;
    });
    return attributes;
  }

  /**
   * Initializes IntersectionObservers for a given object and its associated HTML element.
   *
   * This method sets up observer:
   * - A "progress" observer to track when the object enters or leaves the viewport.
   *
   * The observers are configured with custom root margins and thresholds based on the object's properties.
   * Existing observers, if any, are disconnected before creating new ones.
   *
   * @param obj - The `StringObject` instance containing properties and methods for interaction.
   * @param el - The `HTMLElement` to observe for intersection changes.
   */
  private initObservers(obj: StringObject, el: HTMLElement) {
    if (!this.intersectionObserverEnabled) {
      return;
    }
    const start = obj.getProperty<number>("offset-top") ?? 0;
    const end = obj.getProperty<number>("offset-bottom") ?? 0;
    obj.getProperty<IntersectionObserver>("observer-progress")?.disconnect();
    const progressCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((e) => {
        this.events.emit(`object:activate:${obj.id}`, e.isIntersecting);
        e.isIntersecting ? obj.enter() : obj.leave();
      });
    };
    const outsideProp = obj.getProperty<boolean>("outside-container");
    const outsideAttrValue =
      el.getAttribute("string-outside-container") ??
      el.getAttribute("data-string-outside-container");
    const outsideAttrNormalized =
      outsideAttrValue != null ? outsideAttrValue.trim().toLowerCase() : null;
    const outsideAttrFlag =
      outsideAttrNormalized === "" ||
      outsideAttrNormalized === "true" ||
      outsideAttrNormalized === "1";
    const isOutsideContainer = outsideProp != null ? outsideProp === true : outsideAttrFlag;
    const observerRoot =
      this.data.scroll.container === document.body || isOutsideContainer
        ? null
        : this.data.scroll.container;

    const progressObserver = new IntersectionObserver(progressCallback, {
      root: observerRoot,
      rootMargin: `${end + this.data.viewport.windowHeight}px 0px ${
        start + this.data.viewport.windowHeight
      }px 0px`,
      threshold: 0,
    });
    progressObserver.observe(el);
    obj.setProperty("observer-progress", progressObserver);
  }

  /**
   * Observes DOM mutations to auto-add/remove elements with [string] attribute.
   * Should be called once after DOM is ready.
   */
  public observeDOM(): void {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          // Removed elements
          mutation.removedNodes.forEach((node) => {
            if (node.nodeType !== Node.ELEMENT_NODE) return;

            const element = node as HTMLElement;

            this.detachMirrorByElement(element);

            if (this.isFixed(element)) return;

            if (element.hasAttribute("string")) {
              this.handleRemoved(element);
            }

            element.querySelectorAll("[string],[data-string]").forEach((child) => {
              if (this.isFixed(child as HTMLElement)) return;
              this.handleRemoved(child as HTMLElement);
            });

            element
              .querySelectorAll("[string-copy-from],[data-string-copy-from]")
              .forEach((child) => this.detachMirrorByElement(child as HTMLElement));
          });

          // Added elements
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType !== Node.ELEMENT_NODE) return;

            const element = node as HTMLElement;

            if (this.isFixed(element)) return;

            if (element.hasAttribute("string") && !element.hasAttribute("string-inited")) {
              this.add(element);
            }

            element
              .querySelectorAll("[string]:not([string-inited]),[data-string]:not([string-inited])")
              .forEach((child) => this.add(child as HTMLElement));

            // Check for connect-from logic
            const copyFrom =
              element.getAttribute("string-copy-from") ??
              element.getAttribute("data-string-copy-from");

            if (copyFrom) {
              this.linkMirror(copyFrom, element);
            }

            element
              .querySelectorAll("[string-copy-from],[data-string-copy-from]")
              .forEach((child) => {
                const childCopyFrom =
                  child.getAttribute("string-copy-from") ??
                  child.getAttribute("data-string-copy-from");
                if (childCopyFrom) {
                  this.linkMirror(childCopyFrom, child as HTMLElement);
                }
              });
          });

          if (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0) {
            this.modules.onDOMMutate(mutation.addedNodes, mutation.removedNodes);
          }

          // Let modules know about DOM rebuild
          this.modules.all.forEach((m) => m.onDOMRebuild());
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Removes an object and its observers.
   */
  private handleRemoved(el: HTMLElement): void {
    const id = el.getAttribute("string-id") ?? el.getAttribute("data-string-id");
    if (!id) return;

    const copyFrom =
      el.getAttribute("string-copy-from") ?? el.getAttribute("data-string-copy-from");
    if (copyFrom) {
      this.connectQueue = this.connectQueue.filter((q) => q.id !== copyFrom);
    }

    this.remove(id);
  }

  /**
   * Re-applies module initialization logic to all managed objects after settings change.
   *
   * This method should be called when `StringSettings` are updated at runtime,
   * especially if the new settings affect how modules calculate offsets,
   * easing, origins, or custom configuration.
   *
   * Internally, it re-runs `initializeObject`, `calculatePositions`, and `connectObject`
   * for each core module that can connect to the object.
   *
   * This is useful for supporting dynamic configuration updates without requiring
   * a full DOM rebuild or reinitialization.
   */
  public onSettingsChange(data: ISettingsChangeData) {
    this.objects.forEach((object) => {
      this.modules.core.forEach((m) => {
        let isCanRebuild = false;
        if (data.isDesktop) {
          if (m.permissions.desktop.rebuild.scrollHeight && data.scrollHeightChanged) {
            isCanRebuild = true;
          }
          if (m.permissions.desktop.rebuild.width && data.widthChanged) {
            isCanRebuild = true;
          }
          if (m.permissions.desktop.rebuild.height && data.heightChanged) {
            isCanRebuild = true;
          }
        } else {
          if (m.permissions.mobile.rebuild.scrollHeight && data.scrollHeightChanged) {
            isCanRebuild = true;
          }
          if (m.permissions.mobile.rebuild.width && data.widthChanged) {
            isCanRebuild = true;
          }
          if (m.permissions.mobile.rebuild.height && data.heightChanged) {
            isCanRebuild = true;
          }
        }

        if (isCanRebuild || data.isForceRebuild) {
          if (m.canConnect(object)) {
            const attributes = this.getAllAttributes(object.htmlElement);
            m.initializeObject(this.globalId, object, object.htmlElement, attributes);
            m.calculatePositions(object, this.data.viewport.windowHeight);
            m.connectObject(object);
          }
        }
      });
    });
    this.inviewIndexDirty = true;
  }

  /**
   * Checks whether the element is marked as fixed (not managed).
   */
  private isFixed(el: HTMLElement): boolean {
    return el.hasAttribute("string-fixed");
  }

  public checkInview() {
    const scrollPos = this.data.scroll.transformedCurrent;
    this.updateInviewWindow(scrollPos);
    this.inviewActive.forEach((object) => {
      this.checkInviewForObject(object);
    });
  }

  private checkInviewForObject(object: StringObject) {
    const scrollPos = this.data.scroll.transformedCurrent;
    if (!this.intersectionObserverEnabled) {
      const activeStart = object.getProperty<number>("start-position");
      const activeEnd = object.getProperty<number>("end-position");
      if (activeStart != null && activeEnd != null) {
        const activeMin = Math.min(activeStart, activeEnd);
        const activeMax = Math.max(activeStart, activeEnd);
        const wasActive = object.getProperty<boolean>("is-active") ?? false;
        const isActive = scrollPos >= activeMin && scrollPos <= activeMax;
        if (isActive !== wasActive) {
          object.setProperty("is-active", isActive);
          this.events.emit(`object:activate:${object.id}`, isActive);
          isActive ? object.enter() : object.leave();
        }
      }
    }
    const inviewStart = object.getProperty<number>("inview-start-position");
    const inviewEnd = object.getProperty<number>("inview-end-position");
    const wasInView = object.getProperty<boolean>("is-inview") ?? false;

    const start = Math.min(inviewStart, inviewEnd);
    const end = Math.max(inviewStart, inviewEnd);
    const isNowInView = scrollPos >= start && scrollPos <= end;

    let direction: "enter-top" | "enter-bottom" | "exit-top" | "exit-bottom" | null = null;
    if (!wasInView && isNowInView) {
      const distToStart = Math.abs(scrollPos - start);
      const distToEnd = Math.abs(end - scrollPos);
      direction = distToStart <= distToEnd ? "enter-top" : "enter-bottom";
    } else if (wasInView && !isNowInView) {
      direction = scrollPos < start ? "exit-top" : "exit-bottom";
    }
    if (isNowInView !== wasInView) {
      object.setProperty("is-inview", isNowInView);
      this.events.emit(`object:inview:${object.id}`, {
        inView: isNowInView,
        direction,
      });
      isNowInView ? object.show() : object.hide();
    }
  }

  private updateInviewWindow(scrollPos: number) {
    const buffer = this.data.viewport.windowHeight;
    const windowMin = scrollPos - buffer;
    const windowMax = scrollPos + this.data.viewport.windowHeight + buffer;

    if (this.inviewIndexDirty || scrollPos < this.lastInviewScrollPos) {
      this.rebuildInviewIndex(windowMin, windowMax);
    }

    while (
      this.inviewStartIdx < this.inviewStarts.length &&
      this.inviewStarts[this.inviewStartIdx].pos <= windowMax
    ) {
      this.inviewActive.add(this.inviewStarts[this.inviewStartIdx].object);
      this.inviewStartIdx++;
    }

    while (
      this.inviewEndIdx < this.inviewEnds.length &&
      this.inviewEnds[this.inviewEndIdx].pos < windowMin
    ) {
      this.inviewActive.delete(this.inviewEnds[this.inviewEndIdx].object);
      this.inviewEndIdx++;
    }

    this.lastInviewScrollPos = scrollPos;
  }

  private rebuildInviewIndex(windowMin: number, windowMax: number) {
    this.inviewStarts = [];
    this.inviewEnds = [];

    this.objects.forEach((object) => {
      const start = object.getProperty<number>("inview-start-position");
      const end = object.getProperty<number>("inview-end-position");
      if (start == null || end == null) return;
      this.inviewStarts.push({ pos: Math.min(start, end), object });
      this.inviewEnds.push({ pos: Math.max(start, end), object });
    });

    this.inviewStarts.sort((a, b) => a.pos - b.pos);
    this.inviewEnds.sort((a, b) => a.pos - b.pos);

    this.inviewActive.clear();

    this.inviewStartIdx = this.upperBound(this.inviewStarts, windowMax);
    this.inviewEndIdx = this.upperBound(this.inviewEnds, windowMin - 1);

    for (let i = 0; i < this.inviewStartIdx; i++) {
      this.inviewActive.add(this.inviewStarts[i].object);
    }
    for (let i = 0; i < this.inviewEndIdx; i++) {
      this.inviewActive.delete(this.inviewEnds[i].object);
    }

    this.inviewIndexDirty = false;
  }

  private upperBound(list: Array<{ pos: number }>, value: number): number {
    let low = 0;
    let high = list.length;
    while (low < high) {
      const mid = (low + high) >>> 1;
      if (list[mid].pos <= value) low = mid + 1;
      else high = mid;
    }
    return low;
  }
}
